import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Users, Trash2, Pencil, Mail, Phone } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

export default function Tenants() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [editingId, setEditingId] = useState(null);

  const { data: items = [] } = useQuery({
    queryKey: ["tenants"],
    queryFn: async () => (await api.get("/tenants")).data,
  });

  const save = useMutation({
    mutationFn: (payload) => (editingId ? api.put(`/tenants/${editingId}`, payload) : api.post("/tenants", payload)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tenants"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      setOpen(false);
      setForm({});
      setEditingId(null);
      toast.success("OK");
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Error"),
  });

  const remove = useMutation({
    mutationFn: (id) => api.delete(`/tenants/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tenants"] }),
  });

  const openCreate = () => { setEditingId(null); setForm({}); };
  const openEdit = (x) => {
    setEditingId(x.id);
    setForm({ name: x.name, email: x.email || "", phone: x.phone || "", notes: x.notes || "", nif: x.nif || "" });
    setOpen(true);
  };

  const submit = () => {
    if (!form.name) return toast.error(t("name"));
    save.mutate({ name: form.name, email: form.email || "", phone: form.phone || "",
                 notes: form.notes || "", nif: form.nif || null });
  };

  return (
    <div className="space-y-6" data-testid="tenants-page">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-4xl">{t("nav_tenants")}</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreate} data-testid="add-tenant-btn" className="rounded-sm gap-2">
              <Plus className="h-4 w-4" /> <span className="hidden sm:inline">{t("add_tenant")}</span>
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-popover">
            <DialogHeader><DialogTitle className="font-display text-2xl">{editingId ? t("edit_tenant") : t("add_tenant")}</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2"><Label>{t("name")}</Label>
                <Input data-testid="tenant-name" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm" /></div>
              <div className="space-y-2"><Label>{t("email")}</Label>
                <Input data-testid="tenant-email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-sm" /></div>
              <div className="space-y-2"><Label>{t("phone")}</Label>
                <Input data-testid="tenant-phone" value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-sm" /></div>
              <div className="space-y-2"><Label>{t("nif")}</Label>
                <Input data-testid="tenant-nif" value={form.nif || ""} onChange={(e) => setForm({ ...form, nif: e.target.value })} className="rounded-sm" /></div>
              <div className="space-y-2"><Label>{t("notes")}</Label>
                <Textarea data-testid="tenant-notes" value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-sm" /></div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-sm">{t("cancel")}</Button>
              <Button onClick={submit} data-testid="tenant-save" disabled={save.isPending} className="rounded-sm">{t("save")}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {items.length === 0 ? (
        <Card className="p-16 rounded-sm border-dashed border-border text-center">
          <Users className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">{t("empty_generic")}</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((x) => (
            <Card key={x.id} data-testid={`tenant-card-${x.id}`} className="p-6 rounded-sm border-border group">
              <div className="flex items-start justify-between">
                <div className="h-10 w-10 rounded-full bg-secondary flex items-center justify-center font-mono">{x.name.slice(0, 1).toUpperCase()}</div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button variant="ghost" size="icon" data-testid={`edit-tenant-${x.id}`} onClick={() => openEdit(x)}
                    className="text-muted-foreground hover:text-foreground"><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" data-testid={`del-tenant-${x.id}`} onClick={() => remove.mutate(x.id)}
                    className="text-muted-foreground hover:text-[hsl(var(--destructive))]"><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
              <h3 className="font-display text-xl mt-4">{x.name}</h3>
              {x.email && <p className="text-sm text-muted-foreground flex items-center gap-2 mt-2"><Mail className="h-3 w-3" /> {x.email}</p>}
              {x.phone && <p className="text-sm text-muted-foreground flex items-center gap-2 mt-1"><Phone className="h-3 w-3" /> {x.phone}</p>}
              {x.nif && <p className="text-xs text-muted-foreground mt-1">NIF: {x.nif}</p>}
              {x.notes && <p className="text-sm mt-3 text-muted-foreground">{x.notes}</p>}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
